import React, { useState, useMemo, useContext } from "react";
import styled from "styled-components";
import MuiTable from "common/graphs/Table/Table";
import {
  ColorLabelCell,
  ColorLabelNumberCell,
  NumberCell,
  TimeCell,
} from "common/graphs/Table/TableRenders";
import { SubscriberFlowsResult } from "./SubscriberFlows";
import useProfile from "common/hooks/useProfile";
import colors from "common/graphs/colors";
import { useColorMap } from "./livemetrics.utils";
import BqnContext from "common/BqnContext";

const TableWrapper = styled.div`
  margin: 0 25px;
`;

const WideContainer = styled.div`
  width: 100%;
`;

const HeaderDiv = styled.div`
  display: flex;
  padding: 0 !important;
  &.header {
    border-color: transparent;
  }
  & .title {
    flex: 100% 1 1;
    padding-right: 2.5ch;
    text-align: left;
  }
`;

const H4 = styled.h4`
  flex-grow: 1;
  flex: 0% 1 1 !important;
`;

const PauseContainer = styled.div`
  flex-grow: 1;
  display: flex;
  align-items: center;
`;

const Empty = styled.div`
  width: 100%;
  height: 80px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: "AllianceNo2-SemiBold" !important;
`;

const HIGHLIGHT_COLOR = "#3d85c6";

const BASIC_COLUMNS = [
  {
    label: "MBPS-DN",
    id: "MBPS-DN",
    width: "80px",
    numeric: true,
    renderCell: (value) => {
      const color = value > 0 ? HIGHLIGHT_COLOR : "transparent";
      return (
        <ColorLabelNumberCell
          value={value}
          color={color}
          precision="fourDecimals"
        />
      );
    },
  },
  {
    label: "MBPS-UP",
    id: "MBPS-UP",
    width: "80px",
    numeric: true,
    renderCell: (value) => {
      const color = value > 0 ? HIGHLIGHT_COLOR : "transparent";
      return (
        <ColorLabelNumberCell
          value={value}
          color={color}
          precision="fourDecimals"
        />
      );
    },
  },
  {
    label: "MBYTES-DN",
    id: "MBYTES-DN",
    width: "80px",
    numeric: true,
    renderCell: (value) => (
      <NumberCell precision="fourDecimals" value={value} />
    ),
  },
  {
    label: "MBYTES-UP",
    id: "MBYTES-UP",
    width: "80px",
    numeric: true,
    renderCell: (value) => (
      <NumberCell precision="fourDecimals" value={value} />
    ),
  },
  {
    label: "PROTO",
    id: "PROTO",
    width: "60px",
    numeric: false,
  },
  {
    label: "ACC-ADDR",
    id: "ACC-ADDR",
    width: "110px",
    numeric: false,
  },
  {
    label: "APORT",
    id: "APORT",
    width: "45px",
    numeric: true,
    renderCell: (value) => <NumberCell value={value} hasNA={true} />,
  },
  {
    label: "INET-ADDR",
    id: "INET-ADDR",
    numeric: false,
    width: "110px",
  },
  {
    label: "IPORT",
    id: "IPORT",
    width: "35px",
    numeric: true,
    renderCell: (value) => <NumberCell value={value} hasNA={true} />,
  },
  {
    label: "ST",
    id: "ST",
    width: "35px",
    numeric: false,
  },
  {
    label: "LIFETIME",
    id: "LIFETIME",
    width: "80px",
    renderCell: (value) => <TimeCell value={value} />,
  },
];

const parseResponse = (response) => {
  const [header, ...rows] = response.trim(/\s/).split("\n");
  const headerAttrs = header.split(/\s+/);
  const headerMap = headerAttrs.reduce((acc, attr, index) => {
    acc[index] = attr;
    return acc;
  }, {});
  return rows.reduce((acc, row) => {
    const rowAttrs = row.trim().split(/\s+/);
    const rowObject = rowAttrs.reduce((accRow, attr, index) => {
      accRow[headerMap[index]] = attr;
      return accRow;
    }, {});
    acc.push(rowObject);
    return acc;
  }, []);
};

function LiveSubscriberFlows({
  liveMode,
  setLiveMode,
  response,
  dpiFields,
  target,
  dpiEnabled,
  liveModePaused,
  toggleLiveModePaused,
}) {
  const profile = useProfile() || {};
  const [colorMap, setColorMap] = useColorMap({
    response,
    liveMode,
    dpiFields,
  });

  const {
    clusterStatus: { mode },
  } = useContext(BqnContext);

  const rows = parseResponse(response);
  const columns = useMemo(() => {
    return [
      ...(mode === "cluster"
        ? [
            {
              label: "BQN",
              id: "BQN",
              numeric: true,
              width: "30px",
            },
          ]
        : []),
      ...(profile.dpiEnabled === true
        ? [
            {
              label: "DPI",
              id: "DPI",
              type: "TextColor",
              width: "40px",
              renderCell: (value) => {
                const color = colorMap?.[value] || colors[0];
                return (
                  <ColorLabelCell
                    value={value}
                    color={color}
                  />
                );
              },
            },
            {
              label: "DOMAIN",
              id: "DOMAIN",
              type: "Text",
              width: "100px",
              cellClassName: "domain-column"
            },
            {
              label: "FLOW-POLICY",
              id: "RATE-POLICY",
              type: "Text",
              width: "120px",
              cellClassName: profile.hideRates ? null : "hyperlink-text",
              onClick: profile.hideRates
                ? null
                : (value) => {
                    setLiveMode(false);
                    globalNavigate("viewPolicyFlowEdit", {
                      ...target,
                      returnView: "viewStatusSubscribers",
                      policy: value,
                      dpiEnabled,
                    });
                  },
            },
          ]
        : []),
      ...BASIC_COLUMNS,
    ];
  }, [profile.dpiEnabled, profile.hideRates, JSON.stringify(colorMap)]);

  return (
    <div className="card">
      <div className="body">
        <WideContainer className="live-subscriberflows">
          <HeaderDiv className="header">
            <H4 className="title subtitle margin-0 dpi-table-header">
              Active Flows
            </H4>
            {liveMode && (
              <PauseContainer>
                <button
                  type="button"
                  className="btn btn-default waves-effect shadow-none margin-r-15"
                  onClick={toggleLiveModePaused}
                >
                  <i className="material-icons">
                    {liveModePaused ? "play_arrow" : "pause"}
                  </i>
                  <span> {liveModePaused ? "Resume" : "Pause"} </span>
                </button>
              </PauseContainer>
            )}
          </HeaderDiv>
          {rows.length === 0 ? <Empty>{"No Data Available"}</Empty> : null}
          {rows.length > 0 ? (
            <TableWrapper>
              <MuiTable
                hasSelectNrRows={false}
                isSelectable={false}
                isRowSelectable={false}
                data={rows}
                sort={false}
                columns={columns}
                search={false}
                csvPrefix="active-flows"
                rowsPerPageNr={15}
              />
            </TableWrapper>
          ) : null}
        </WideContainer>
      </div>
    </div>
  );
}
export default LiveSubscriberFlows;
